import './privacy-consent.css';

type DsrStatus = 'queued_local' | 'received' | 'in_progress' | 'completed' | 'rejected'

type DataRequest = {
  id: string
  serverId?: string
  type: 'export' | 'delete'
  status: DsrStatus
  email: string
  reason: string
  requestedAt: string
  lastError?: string
}

type DsrRequestOptions = {
  authToken?: string
  getAuthToken?: () => string | Promise<string>
  baseUrl?: string
  fetchImpl?: typeof fetch
  timeoutMs?: number
  signal?: AbortSignal
}

const CONSENT_STORAGE_KEY = 'agency.privacy.consent.v1';
const REQUEST_STORAGE_KEY = 'agency.privacy.requests.v1';

/** @typedef {'queued_local' | 'received' | 'in_progress' | 'completed' | 'rejected'} DsrStatus */
/** @typedef {{ id: string, serverId?: string, type: 'export' | 'delete', status: DsrStatus, email: string, reason: string, requestedAt: string, lastError?: string }} DataRequest */
/** @typedef {{ authToken?: string, getAuthToken?: () => string | Promise<string>, baseUrl?: string, fetchImpl?: typeof fetch, timeoutMs?: number, signal?: AbortSignal }} DsrRequestOptions */
/** @typedef {Window & { __AGENCY_PRIVACY__?: Record<string, unknown> }} PrivacyWindow */

const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  errorTracking: false,
  marketing: false,
  decidedAt: null
};

const DSR_API_PATH = '/v1/privacy/data-requests';
const CONSENT_BANNER_ID = 'privacy-consent-banner';

function getDsrApiBaseUrl() {
  return String(
    import.meta.env.VITE_PRIVACY_API_BASE_URL ||
      import.meta.env.VITE_GENERATION_API_BASE_URL ||
      ''
  ).replace(/\/+$/, '');
}

function getStorage() {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

export function getConsentState() {
  const storage = getStorage();
  if (!storage) return { ...DEFAULT_CONSENT };
  const raw = storage.getItem(CONSENT_STORAGE_KEY);
  if (!raw) return { ...DEFAULT_CONSENT };
  try {
    return { ...DEFAULT_CONSENT, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_CONSENT };
  }
}

/** @param {Partial<typeof DEFAULT_CONSENT>} patch */
export function setConsentState(patch: Partial<typeof DEFAULT_CONSENT>): typeof DEFAULT_CONSENT {
  const storage = getStorage();
  const next = { ...getConsentState(), ...patch, decidedAt: new Date().toISOString() };
  if (storage) storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('agency:privacy-consent-changed', { detail: next }));
  }
  return next;
}

/** @param {'necessary' | 'analytics' | 'errorTracking' | 'marketing'} category */
export function hasPrivacyConsent(category: string): boolean {
  const state = getConsentState();
  if (category === 'necessary') return true;
  if (category === 'analytics') return Boolean(state.analytics);
  if (category === 'errorTracking') return Boolean(state.errorTracking);
  if (category === 'marketing') return Boolean(state.marketing);
  return false;
}

function readPrivacyRequests(): DataRequest[] {
  const storage = getStorage();
  if (!storage) return [];
  const raw = storage.getItem(REQUEST_STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as DataRequest[] : [];
  } catch {
    return [];
  }
}

/** @param {DataRequest[]} requests */
function writePrivacyRequests(requests: DataRequest[]) {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(REQUEST_STORAGE_KEY, JSON.stringify(requests));
}

/** @param {DataRequest} request */
function upsertLocal(request: DataRequest) {
  const all = readPrivacyRequests();
  const idx = all.findIndex((r) => r.id === request.id);
  if (idx >= 0) all[idx] = request;
  else all.push(request);
  writePrivacyRequests(all);
}

async function resolveAuth() {
  try {
    const mod = await import('./firebase.js');
    return mod.auth;
  } catch {
    return null;
  }
}

/** @param {DsrRequestOptions} [options] */
async function getAuthToken(options: DsrRequestOptions = {}) {
  if (Object.prototype.hasOwnProperty.call(options, 'authToken')) {
    return String(options.authToken || '');
  }
  if (typeof options.getAuthToken === 'function') {
    try {
      return String((await options.getAuthToken()) || '');
    } catch {
      return '';
    }
  }
  const auth = await resolveAuth();
  const user = auth?.currentUser;
  if (!user) return '';
  try {
    return await user.getIdToken();
  } catch {
    return '';
  }
}

/** @param {DataRequest} request @param {DsrRequestOptions} [options] */
async function postDsrToBackend(request: DataRequest, options: DsrRequestOptions = {}) {
  const baseUrl = options.baseUrl ?? getDsrApiBaseUrl();
  if (!baseUrl) {
    throw new Error('privacy_api_not_configured');
  }
  const token = await getAuthToken(options);
  if (!token) {
    throw new Error('not_authenticated');
  }
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const response = await fetchImpl(`${baseUrl}${DSR_API_PATH}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      clientRequestId: request.id,
      type: request.type,
      email: request.email,
      reason: request.reason,
      requestedAt: request.requestedAt
    }),
    signal: options.signal ?? AbortSignal.timeout(options.timeoutMs ?? 15_000)
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`dsr_http_${response.status}:${String(text).slice(0, 120)}`);
  }
  const json = await response.json().catch(() => ({}));
  return {
    serverId: String(json.id || json.requestId || ''),
    status: String(json.status || 'received')
  };
}

/** @param {'export' | 'delete'} type @param {{ email: string, reason?: string }} payload @param {DsrRequestOptions} [options] */
export async function submitDataRequest(type: 'export' | 'delete', payload: { email: string, reason?: string }, options: DsrRequestOptions = {}) {
  if (type !== 'export' && type !== 'delete') {
    throw new Error('invalid_dsr_type');
  }
  const email = String(payload?.email || '').trim().toLowerCase();
  if (!email) {
    throw new Error('email_required');
  }
  const request: DataRequest = {
    id: `privacy_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    type,
    status: 'queued_local',
    email,
    reason: String(payload?.reason || ''),
    requestedAt: new Date().toISOString()
  };
  upsertLocal(request);

  try {
    const ack = await postDsrToBackend(request, options);
    /** @type {DataRequest} */
    const accepted: DataRequest = {
      ...request,
      status: 'received',
      serverId: ack.serverId,
      lastError: undefined
    };
    upsertLocal(accepted);
    return accepted;
  } catch (error) {
    /** @type {DataRequest} */
    const failed: DataRequest = {
      ...request,
      status: 'queued_local',
      lastError: error instanceof Error ? error.message : String(error)
    };
    upsertLocal(failed);
    return failed;
  }
}

/** @param {DsrRequestOptions} [options] */
export async function flushPendingDataRequests(options: DsrRequestOptions = {}) {
  const pending = readPrivacyRequests().filter((r) => r.status === 'queued_local');
  const results = [];
  for (const request of pending) {
    try {
      const ack = await postDsrToBackend(request, options);
      const accepted: DataRequest = {
        ...request,
        status: 'received',
        serverId: ack.serverId,
        lastError: undefined
      };
      upsertLocal(accepted);
      results.push(accepted);
    } catch (error) {
      upsertLocal({
        ...request,
        lastError: error instanceof Error ? error.message : String(error)
      });
    }
  }
  return results;
}

export function listDataRequests() {
  return readPrivacyRequests();
}

function removeConsentBanner() {
  document.getElementById(CONSENT_BANNER_ID)?.remove();
}

function createConsentButton(label: string, className: string, onClick: () => void) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = className;
  button.textContent = label;
  button.addEventListener('click', onClick);
  return button;
}

function mountConsentBanner() {
  if (typeof document === 'undefined') return;
  if (getConsentState().decidedAt) {
    removeConsentBanner();
    return;
  }
  if (document.getElementById(CONSENT_BANNER_ID)) return;

  const banner = document.createElement('section');
  banner.id = CONSENT_BANNER_ID;
  banner.className = 'privacy-consent-banner';
  banner.setAttribute('aria-label', 'Privacy and cookie choices');
  banner.setAttribute('role', 'dialog');

  const copy = document.createElement('div');
  copy.className = 'privacy-consent-copy';

  const title = document.createElement('p');
  title.className = 'privacy-consent-title';
  title.textContent = 'Privacy choices';

  const body = document.createElement('p');
  body.className = 'privacy-consent-body';
  body.textContent =
    'Firebase Authentication uses essential browser storage and cookies to keep sign-in secure. Optional analytics and error tracking stay off unless you allow them.';

  const link = document.createElement('a');
  link.href = '/privacy.html';
  link.className = 'privacy-consent-link';
  link.textContent = 'Privacy details';

  copy.append(title, body, link);

  const actions = document.createElement('div');
  actions.className = 'privacy-consent-actions';
  actions.append(
    createConsentButton('Necessary only', 'privacy-consent-btn privacy-consent-btn-secondary', () => {
      setConsentState({ analytics: false, errorTracking: false, marketing: false });
      removeConsentBanner();
    }),
    createConsentButton('Allow optional', 'privacy-consent-btn privacy-consent-btn-primary', () => {
      setConsentState({ analytics: true, errorTracking: true, marketing: false });
      removeConsentBanner();
    })
  );

  banner.append(copy, actions);
  document.body.appendChild(banner);
}

export function initializePrivacyCompliance() {
  const state = getConsentState();
  if (typeof window !== 'undefined') {
    const typedWindow = /** @type {PrivacyWindow} */ (window);
    typedWindow.__AGENCY_PRIVACY__ = {
      getConsentState,
      setConsentState,
      hasPrivacyConsent,
      submitDataRequest,
      listDataRequests,
      flushPendingDataRequests
    };
    window.addEventListener('agency:privacy-consent-required', mountConsentBanner);
    window.addEventListener('agency:privacy-consent-changed', removeConsentBanner);
    if (!state.decidedAt) {
      window.dispatchEvent(new CustomEvent('agency:privacy-consent-required'));
    } else {
      removeConsentBanner();
    }
    // Best-effort reconciliation when the page boots online with a session.
    void resolveAuth().then((auth) => {
      if (auth?.currentUser) {
        void flushPendingDataRequests();
      }
    });
  }
  return state;
}
